/**
 * Practitioner-related data fetchers
 */
import { QueryClient, useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { createOrUpdatePractitioner, updatePractitioner, deletePractitioner } from "@/src/actions/practitioner";
import { toast } from "@/src/hooks/use-toast";
import { REFERRALS_CACHE_KEY } from "@/src/lib/data-fetchers/referrals-fetchers";
import { TASKS_CACHE_KEY, TASK_CACHE_KEY } from "@/src/lib/data-fetchers/tasks-fetchers";
import { logger } from "@/src/lib/logging/logger";
import { Practitioner } from "@/src/models/practitioner";
import { getAllPatientPractitioners, getAllPractitioners, getPractitionerById } from "@/src/requests/practitioner";
import { isNotFound } from "@/src/requests/utils/fetch-json";

export const PRACTITIONERS_CACHE_KEY = "practitioners";

const deDuplicatePractitioners = (practitioners: Practitioner[]) => {
  // First sort practitioners to prioritize those with NPI numbers
  const sortedPractitioners = [...practitioners].sort((a, b) => {
    if (a.npiNumber && !b.npiNumber) return -1;

    if (!a.npiNumber && b.npiNumber) return 1;

    return 0;
  });

  // Then deduplicate using name and role criteria
  const uniquePractitioners = [];
  const namesSet = new Set();

  for (const practitioner of sortedPractitioners) {
    const nameKey = `${practitioner.givenName}|${practitioner.familyName}|${practitioner.role}`;

    if (!namesSet.has(nameKey)) {
      namesSet.add(nameKey);

      uniquePractitioners.push(practitioner);
    }
  }

  return uniquePractitioners;
};

// Todo: add refetch interval
export function usePractitioners() {
  const query = useQuery({
    queryKey: [PRACTITIONERS_CACHE_KEY],
    queryFn: getAllPractitioners,
    retry: false,
    staleTime: Infinity,
  });

  const practitioners = (() => {
    return deDuplicatePractitioners(Object.values(query.data || {})).sort((a, b) => {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  })();

  return {
    practitionersMap: query.data || {},
    practitioners,
    isFetching: query.isFetching,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

export function usePractitionerById(displayId: string, options?: { enabled?: boolean }) {
  const queryClient = useQueryClient();

  const {
    data: practitioner,
    isFetching,
    isLoading,
    isError,
  } = useQuery({
    queryKey: [PRACTITIONERS_CACHE_KEY, displayId],
    queryFn: async () => {
      // First check if the practitioner is in our global practitioners cache
      const practitionersCache = queryClient.getQueryData<Record<string, Practitioner>>([PRACTITIONERS_CACHE_KEY]);

      if (practitionersCache && practitionersCache[displayId]) {
        logger.info("Found practitioner in cache");

        return practitionersCache[displayId];
      }

      logger.info("Fetching practitioner from server");

      try {
        // If not in cache, fetch from server
        const practitioner = await getPractitionerById(displayId);

        if (practitioner) {
          updatePractitionersQueryData(queryClient, practitioner);
        }

        return practitioner;
      } catch (err) {
        if (isNotFound(err as Error)) {
          return null;
        }

        throw err;
      }
    },
    staleTime: Infinity,
    enabled: options?.enabled !== false && !!displayId,
  });

  return { practitioner, isFetching, isLoading, isError };
}

export function usePatientPractitioners(patientId?: string) {
  const query = useQuery({
    queryKey: [PRACTITIONERS_CACHE_KEY, "patient", patientId],
    queryFn: () => {
      if (!patientId) {
        return {} as Awaited<ReturnType<typeof getAllPatientPractitioners>>;
      }

      return getAllPatientPractitioners(patientId);
    },
    enabled: Boolean(patientId),
    staleTime: Infinity,
    refetchInterval: 60 * 1000,
  });

  return {
    patientPractitionersMap: query.data || {},
    patientPractitioners: Object.values(query.data || {}).sort((a, b) => {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    }),
    isFetching: query.isFetching,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

export function usePractitionerUpdateMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (practitioner: Practitioner) => {
      const result = await updatePractitioner(practitioner.id, practitioner);

      if (!result) {
        throw new Error("Failed to update practitioner");
      }

      return result;
    },
    onMutate: async (practitioner) => {
      await queryClient.cancelQueries({ queryKey: [PRACTITIONERS_CACHE_KEY] });

      const previousPractitioners =
        queryClient.getQueryData<Record<string, Practitioner>>([PRACTITIONERS_CACHE_KEY]) ?? {};

      const practitionersCache =
        queryClient.getQueryData<Record<string, Practitioner>>([PRACTITIONERS_CACHE_KEY]) ?? {};

      if (practitionersCache[practitioner.id]) {
        updatePractitionersQueryData(queryClient, practitioner);
      }

      return { previousPractitioners };
    },
    onError: (error, variables, context) => {
      logger.error("Error updating practitioner");

      if (context) {
        queryClient.setQueryData([PRACTITIONERS_CACHE_KEY], context.previousPractitioners);

        if (variables.displayId && context.previousPractitioners[variables.displayId]) {
          queryClient.setQueryData(
            [PRACTITIONERS_CACHE_KEY, variables.displayId],
            context.previousPractitioners[variables.displayId]
          );
        }

        if (variables.id && context.previousPractitioners[variables.id]) {
          queryClient.setQueryData(
            [PRACTITIONERS_CACHE_KEY, variables.id],
            context.previousPractitioners[variables.id]
          );
        }
      }

      toast({
        title: "Failed to save practitioner",
        description: "There was an error saving the practitioner. Please try again.",
        variant: "destructive",
      });
    },
    meta: {
      invalidates: [[REFERRALS_CACHE_KEY], [TASKS_CACHE_KEY], [TASK_CACHE_KEY]],
    },
  });
}

export const updatePractitionersQueryData = (queryClient: QueryClient, updatedPractitioner: Practitioner) => {
  queryClient.setQueryData([PRACTITIONERS_CACHE_KEY], (oldData: Record<string, Practitioner> = {}) => {
    return {
      ...oldData,
      [updatedPractitioner.id]: updatedPractitioner,
    };
  });

  if (updatedPractitioner.displayId) {
    queryClient.setQueryData([PRACTITIONERS_CACHE_KEY, updatedPractitioner.displayId], updatedPractitioner);
  }

  if (updatedPractitioner.id) {
    queryClient.setQueryData([PRACTITIONERS_CACHE_KEY, updatedPractitioner.id], updatedPractitioner);
  }
};

export function usePractitionerCreateOrUpdateMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (practitioner: Practitioner) => {
      const result = await createOrUpdatePractitioner(practitioner);

      if (!result) {
        throw new Error("Failed to create practitioner");
      }

      return result;
    },
    onMutate: async (practitioner) => {
      await queryClient.cancelQueries({ queryKey: [PRACTITIONERS_CACHE_KEY] });

      const previousPractitioners =
        queryClient.getQueryData<Record<string, Practitioner>>([PRACTITIONERS_CACHE_KEY]) ?? {};

      // Optimistically upsert the practitioner
      updatePractitionersQueryData(queryClient, practitioner);

      return { previousPractitioners };
    },
    onSuccess: (savedPractitioner) => {
      // Reconcile cache with saved result
      updatePractitionersQueryData(queryClient, savedPractitioner);
    },
    onError: (error, _variables, context) => {
      logger.error("Error creating practitioner");

      if (context) {
        queryClient.setQueryData([PRACTITIONERS_CACHE_KEY], context.previousPractitioners);
      }

      toast({
        title: "Failed to create practitioner",
        description: "There was an error creating the practitioner. Please try again.",
        variant: "destructive",
      });
    },
    meta: {
      invalidates: [[REFERRALS_CACHE_KEY], [TASKS_CACHE_KEY], [TASK_CACHE_KEY]],
    },
  });
}

export function useDeletePractitionerMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ practitionerId }: { practitionerId: string }) => {
      return deletePractitioner({ practitionerId });
    },
    onMutate: async ({ practitionerId }) => {
      await queryClient.cancelQueries({ queryKey: [PRACTITIONERS_CACHE_KEY] });

      const previousPractitioners =
        queryClient.getQueryData<Record<string, Practitioner>>([PRACTITIONERS_CACHE_KEY]) ?? {};

      queryClient.setQueryData([PRACTITIONERS_CACHE_KEY], (practitioners: Record<string, Practitioner> = {}) => {
        const updated = { ...practitioners };

        delete updated[practitionerId];

        return updated;
      });

      // Also remove from individual cache entries
      queryClient.removeQueries({ queryKey: [PRACTITIONERS_CACHE_KEY, practitionerId] });

      return { previousPractitioners };
    },
    onError: (error, { practitionerId }, context) => {
      if (context) {
        queryClient.setQueryData([PRACTITIONERS_CACHE_KEY], context.previousPractitioners);
      }

      logger.error("Error deleting practitioner:", {
        practitionerId,
        error,
      });

      toast({
        title: "Practitioner deletion failed",
        description: "There was an error deleting the practitioner. Please try again.",
        variant: "destructive",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PRACTITIONERS_CACHE_KEY] });
      toast({
        title: "Practitioner deleted successfully",
      });
    },
    meta: {
      invalidates: [[REFERRALS_CACHE_KEY], [TASKS_CACHE_KEY], [TASK_CACHE_KEY]],
    },
  });
}
